<template>
  <SCASidebarNavigationItem
    :label="typeof label === 'string' ? localizeModuleName(label) : label"
    :icon="icon"
    :expandable="props.expandable || hasChildren"
    :expanded="expanded || Boolean(layoutStore.get(expandedKey).expanded)"
    :selected="!isCreateMode && isActive"
    :is-active="!isCreateMode && isNodeActive(props.nav, route, router, false)"
    :as="!props.onItemClick && to && !props.disabled ? RouterLink : undefined"
    :to="!props.onItemClick && !props.disabled ? to : undefined"
    :level="level"
    v-bind="$attrs"
    :disabled="props.disabled"
    :favorite="layoutStore.isFavorite(props.storeKey ?? '')"
    :is-mini
    @update:expanded="onUpdateExpanded"
    @click="props.nav?.onItemClick?.()"
  >
    <slot :parent-store-key="expandedKey" />
    <template v-if="actions" #actions>
      <SidebarNavActions
        :to="to"
        :favorite-key="props.storeKey"
        :can-favorite="props.nav?.canFavorite !== false"
        :module-label="props.moduleLabel"
        :template-label="typeof label === 'string' ? localizeModuleName(label) : unhighlightMatch(label)"
      />
    </template>
  </SCASidebarNavigationItem>
</template>

<script setup lang="ts">
import type { TemplateInfo } from "@scayla/api-client-fetch";
import { SCASidebarNavigationItem } from "@scayla/components";
import type { HighlightedText } from "@scayla/core";
import { unhighlightMatch } from "@scayla/core";
import { computed, useSlots } from "vue";
import type { RouteLocationAsRelativeGeneric } from "vue-router";
import { RouterLink, useRoute, useRouter } from "vue-router";

import { localizeModuleName } from "@/i18n/helpers";
import { useLayoutStore } from "@/store/layoutStore";
import type { NavNode } from "@/utils/navUtils";
import { isNodeActive } from "@/utils/navUtils";

import SidebarNavActions from "./SidebarNavActions.vue";

const props = withDefaults(
  defineProps<{
    label: string | HighlightedText;
    icon?: string;
    storeKey?: string;
    parentStoreKey?: string;
    to?: RouteLocationAsRelativeGeneric;
    level?: number;
    actions?: boolean;
    expanded?: boolean;
    expandable?: boolean;
    isCreateMode?: boolean;
    disabled?: boolean;
    nav?: NavNode;
    moduleLabel?: string;
    isMini?: boolean;
    onItemClick?: (template: TemplateInfo) => void;
  }>(),
  {
    storeKey: undefined,
    parentStoreKey: undefined,
    icon: undefined,
    to: undefined,
    level: 0,
    actions: true,
    expanded: false,
    expandable: false,
    isCreateMode: false,
    disabled: false,
    nav: undefined,
    moduleLabel: undefined,
    isMini: false,
    onItemClick: undefined,
  },
);

const slots = useSlots();
const layoutStore = useLayoutStore();
const route = useRoute();
const router = useRouter();

// Memoized computations to avoid expensive recalculations
const expandedKey = computed(() => [props.parentStoreKey, props.storeKey].filter(Boolean).join("."));
const hasChildren = computed(() => !!slots.default);

const isActive = computed(() => (props.nav ? isNodeActive(props.nav, route, router) : false));

function onUpdateExpanded(v: boolean) {
  layoutStore.set(expandedKey.value, v);
}
</script>
